import { useState } from "react";
import dayjs from "dayjs";
import {
  Card, Table, Button, Modal, Form, Input, Switch, InputNumber,
  Tag, Popconfirm, message, Select, Alert, DatePicker,
} from "antd";
import { Plus, Pencil, Trash2, Smartphone } from "lucide-react";
import { useAppVersionList, useAppVersionSave, useAppVersionDelete } from "../../../api-hooks/appVersions";
import { TAppVersion, TAppVersionPlatform } from "../../../types";
import type { ColumnsType } from "antd/es/table";

const { Option } = Select;

const PLATFORM_COLOR: Record<TAppVersionPlatform, string> = {
  ios: "blue",
  android: "green",
};

const PLATFORM_LABEL: Record<TAppVersionPlatform, string> = {
  ios: "iOS",
  android: "Android",
};

export default function AppVersions() {
  const [showModal, setShowModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState<TAppVersion | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [messageApi, ctx] = message.useMessage();
  const [form] = Form.useForm();

  // Fetch all versions; filtering done client-side since total records are small
  const { data: versions = [], isLoading } = useAppVersionList({ limit: 200 });

  const { mutate: saveVersion, isPending: saving } = useAppVersionSave({
    onSuccess: () => {
      messageApi.success(editingVersion ? "Version updated." : "Version added.");
      setShowModal(false);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to save version."),
  });

  const { mutate: deleteVersion } = useAppVersionDelete({
    onSuccess: () => messageApi.success("Version deleted."),
    onError: () => messageApi.error("Failed to delete version."),
  });

  const filtered = platformFilter === "all"
    ? versions
    : versions.filter((v) => v.platform === platformFilter);

  const openCreate = () => {
    setEditingVersion(null);
    form.resetFields();
    form.setFieldsValue({ platform: "ios", force_update: false, is_latest: true });
    setShowModal(true);
  };

  const openEdit = (version: TAppVersion) => {
    setEditingVersion(version);
    form.setFieldsValue({
      ...version,
      released_at: version.released_at ? dayjs(version.released_at) : null,
    });
    setShowModal(true);
  };

  const handleSubmit = (vals: any) => {
    if (vals.released_at && typeof vals.released_at !== "string") {
      vals.released_at = vals.released_at.toISOString();
    }
    saveVersion({ ...vals, id: editingVersion?.id });
  };

  const columns: ColumnsType<TAppVersion> = [
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (val: TAppVersionPlatform) => (
        <Tag color={PLATFORM_COLOR[val]}>{PLATFORM_LABEL[val]}</Tag>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      render: (val: string) => (
        <span className="font-mono text-sm font-semibold text-slate-800">{val}</span>
      ),
    },
    {
      title: "Build #",
      dataIndex: "build_number",
      key: "build_number",
      render: (val: number) => <span className="text-sm text-slate-500">{val}</span>,
    },
    {
      title: "Min Supported",
      dataIndex: "min_supported_version",
      key: "min_supported_version",
      render: (val: string) => (
        // min_supported_version: users on any version BELOW this will be force-blocked from the app
        <span className="font-mono text-xs text-slate-500">{val}</span>
      ),
    },
    {
      title: "Latest",
      dataIndex: "is_latest",
      key: "is_latest",
      align: "center" as const,
      render: (val: boolean) => (
        // is_latest: this version is served to devices calling the "check for update" endpoint
        val ? <Tag color="green">Latest</Tag> : <Tag color="default">—</Tag>
      ),
    },
    {
      title: "Force Update",
      dataIndex: "force_update",
      key: "force_update",
      align: "center" as const,
      render: (val: boolean) => (
        // force_update: if true, users on older versions are blocked until they update
        val ? <Tag color="red">Forced</Tag> : <Tag color="default">Optional</Tag>
      ),
    },
    {
      title: "Released",
      dataIndex: "released_at",
      key: "released_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleDateString("en-GB")}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TAppVersion) => (
        <div className="flex gap-2 justify-end">
          <Button size="small" icon={<Pencil size={12} />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this version?"
            description="If this is the latest version, devices won't get an update target."
            onConfirm={() => deleteVersion(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<Trash2 size={12} />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">App Versions</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Manage iOS and Android release versions, minimum supported versions, and force-update flags
          </p>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={openCreate}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
        >
          New Version
        </Button>
      </div>

      {/* Platform filter */}
      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm text-slate-500">Platform:</span>
        <Select value={platformFilter} onChange={setPlatformFilter} style={{ width: 160 }} size="middle">
          <Option value="all">All Platforms</Option>
          <Option value="ios">iOS</Option>
          <Option value="android">Android</Option>
        </Select>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No pagination — version history is small, show all records */}
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          size="middle"
          locale={{ emptyText: "No versions recorded yet." }}
        />
      </Card>

      <Modal
        open={showModal}
        onCancel={() => { setShowModal(false); form.resetFields(); }}
        footer={null}
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Smartphone size={15} className="text-violet-600" />
            </div>
            <span>{editingVersion ? `Edit: ${editingVersion.version}` : "Add App Version"}</span>
          </div>
        }
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="mt-4">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="platform"
              label="Platform"
              rules={[{ required: true, message: "Platform is required" }]}
            >
              <Select>
                <Option value="ios">iOS</Option>
                <Option value="android">Android</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="version"
              label="Version"
              rules={[
                { required: true, message: "Version is required" },
                { pattern: /^\d+\.\d+\.\d+$/, message: "Format: major.minor.patch (e.g. 2.4.1)" },
              ]}
            >
              <Input className="rounded-lg font-mono" placeholder="2.4.1" />
            </Form.Item>

            <Form.Item
              name="build_number"
              label="Build Number"
              rules={[{ required: true, message: "Build number is required" }, { type: "number", min: 1 }]}
            >
              <InputNumber min={1} className="w-full rounded-lg" placeholder="241" />
            </Form.Item>

            <Form.Item
              name="min_supported_version"
              label="Min Supported Version"
              extra="Users below this are blocked"
              rules={[
                { required: true, message: "Required" },
                { pattern: /^\d+\.\d+\.\d+$/, message: "Format: major.minor.patch" },
              ]}
            >
              <Input className="rounded-lg font-mono" placeholder="2.0.0" />
            </Form.Item>

            <Form.Item
              name="released_at"
              label="Released At"
              rules={[{ required: true, message: "Release date is required" }]}
              className="col-span-2"
            >
              <DatePicker showTime className="w-full rounded-lg" format="YYYY-MM-DD HH:mm:ss" />
            </Form.Item>
          </div>

          <Form.Item name="release_notes" label="Release Notes">
            <Input.TextArea
              className="rounded-lg"
              rows={3}
              placeholder="What changed in this release..."
              maxLength={2000}
              showCount
            />
          </Form.Item>

          {/* force_update blocks users on versions below min_supported_version from using the app */}
          <Alert
            type="warning"
            showIcon
            className="mb-4 rounded-xl"
            message="About Force Update"
            description="When enabled, users running a version below the minimum supported version will be blocked from using the app until they update."
          />

          <div className="flex gap-8 py-2 px-3 bg-slate-50 rounded-xl mb-4">
            <Form.Item name="is_latest" valuePropName="checked" label="Is Latest" className="mb-0">
              {/* is_latest: set to true to serve this version as the update target for devices checking for updates */}
              <Switch />
            </Form.Item>
            <Form.Item name="force_update" valuePropName="checked" label="Force Update" className="mb-0">
              {/* force_update: when true, any user below min_supported_version is hard-blocked */}
              <Switch />
            </Form.Item>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
            <Button onClick={() => { setShowModal(false); form.resetFields(); }}>Cancel</Button>
            <Button
              htmlType="submit"
              type="primary"
              loading={saving}
              style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
            >
              {editingVersion ? "Update Version" : "Add Version"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
